import api from './api';

export interface HistorianQueryParams {
  siteIds: string[];
  assetType?: string;
  deviceIds?: string[];
  metrics: string[];
  startTime: string;
  endTime: string;
  interval: string;
  limit?: number;
}

// Historian queries can legitimately take longer than the 10s global
// axios default — a 30d query at 1d interval still involves a rollup read
// and network round-trip. 60s is a safety margin on top of the backend's
// continuous-aggregate routing, which keeps most queries sub-second.
const HISTORIAN_QUERY_TIMEOUT_MS = 60_000;

export interface HistorianRow {
  timestamp: string;
  [key: string]: string | number;
}

/**
 * Per-column metadata emitted alongside the pivoted wide result. Keyed by the
 * same column name as `columns[]`/row keys (flat `metric` or namespaced
 * `metric::device_id`). Consumed by the chart render to group series onto
 * dual Y-axes by unit and to resolve per-series styling.
 *
 * `unit` is null when the backend path can't supply it (continuous-aggregate
 * rollup path). The chart falls back to the static METRIC_UNITS catalog in
 * web/src/utils/historianMetrics.ts.
 */
export interface HistorianColumnMeta {
  metric: string;
  device_id: string | null;
  unit: string | null;
}

export interface HistorianResult {
  columns: string[];
  rows: HistorianRow[];
  /**
   * Optional — older backend responses won't include this. Consumers must
   * null-check and fall back to parsing column names + the METRIC_UNITS
   * catalog when absent.
   */
  columnMeta?: Record<string, HistorianColumnMeta>;
  metadata: {
    row_count: number;
    query_time_ms: number;
    interval: string;
    start: string;
    end: string;
  };
}

/**
 * Structured failure body returned by every `/v1/historian/*` error response.
 * See backend `historian.routes.ts` / issue #128.
 *
 * The `code` field is stable across releases; `message` and `hint` are
 * human-readable and safe to show directly in a toast.
 */
export interface HistorianErrorBody {
  code: string;
  message: string;
  hint?: string;
}

/** Shape-test a candidate error body. Returns it typed, or null if it isn't one. */
function asHistorianErrorBody(candidate: unknown): HistorianErrorBody | null {
  if (
    typeof candidate === 'object' &&
    candidate !== null &&
    'code' in candidate &&
    'message' in candidate &&
    typeof (candidate as { code: unknown }).code === 'string' &&
    typeof (candidate as { message: unknown }).message === 'string'
  ) {
    return candidate as HistorianErrorBody;
  }
  return null;
}

/**
 * Pull a structured historian error out of whatever axios / fetch threw.
 * Falls back to a synthetic `{ code: 'network', ... }` body when the wire
 * failed before the server could respond (e.g. the Container Apps ingress
 * idle-cut the request). Never throws.
 *
 * ⚠️ TWO wire envelopes are accepted, because the backend genuinely sends two
 * (verified against `backend/src/routes/historian.routes.ts`, djin-cloud#806):
 *
 *   1. `{ success: false, error: { code, message, hint } }` — every historian
 *      route's ordinary failure path, via `classifyHistorianError`.
 *   2. A FLAT `{ code, message, hint }` — the `SeriesSpecError` branch of
 *      `POST /v1/historian/series-query` only, and deliberately so: that body
 *      is the spec's own error shape, shared with the future assistant tool,
 *      so the route returns it unwrapped.
 *
 * Reading only (1) is not a cosmetic miss. A spec validation failure — too
 * many series, a bad alias, a range over 366 days — is the error an operator
 * is MOST likely to hit, and it would have surfaced as
 * "The server did not respond with a structured error" while the structured
 * error sat in the response body. The envelope is checked first so a route
 * that legitimately nests a body is never shadowed by a stray top-level
 * `code` field.
 */
export function extractHistorianError(err: unknown): HistorianErrorBody {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const resp = (err as { response?: { data?: unknown } }).response;
    const data = resp?.data;

    const enveloped = asHistorianErrorBody(
      typeof data === 'object' && data !== null && 'error' in data
        ? (data as { error: unknown }).error
        : undefined
    );
    if (enveloped) return enveloped;

    const flat = asHistorianErrorBody(data);
    if (flat) return flat;
  }
  const message =
    typeof err === 'object' &&
    err !== null &&
    'message' in err &&
    typeof (err as { message: unknown }).message === 'string'
      ? (err as { message: string }).message
      : 'Unknown error';
  return {
    code: 'network',
    message,
    hint: 'The server did not respond with a structured error. Try again in a moment.',
  };
}

// ⚠️ `queryHistorian`, `fetchMetrics` and `fetchAssetTypes` below have ZERO
// consumers in web/src outside this file — not even a test — as of the
// djin-cloud#806 Historian rebuild (PR 7 head). The rebuilt page
// (`pages/Historian.tsx`) reads through `HistorianContext`, which calls
// `fetchCatalog`/`runSeriesQuery`/`fetchPersistedQuery` further down this
// file instead. Kept rather than deleted because they are a thin, correct
// wrapper over the still-live `POST /v1/historian/query`,
// `GET /v1/historian/metrics/:siteId` and `GET /v1/historian/asset-types/:siteId`
// routes (see `backend/src/routes/historian.routes.ts`) — a future non-UI
// caller (a report, a deep-link fallback) could reach for them — but no such
// caller exists today. Do not cite one that doesn't; if you add one, delete
// this comment.
export async function queryHistorian(params: HistorianQueryParams): Promise<HistorianResult> {
  const res = await api.post<{ success: boolean; data: HistorianResult }>(
    '/v1/historian/query',
    params,
    { timeout: HISTORIAN_QUERY_TIMEOUT_MS }
  );
  return res.data.data;
}

export async function fetchMetrics(siteId: string): Promise<string[]> {
  const res = await api.get<{ success: boolean; data: string[] }>(
    `/v1/historian/metrics/${encodeURIComponent(siteId)}`
  );
  return res.data.data;
}

export async function fetchAssetTypes(siteId: string): Promise<string[]> {
  const res = await api.get<{ success: boolean; data: string[] }>(
    `/v1/historian/asset-types/${encodeURIComponent(siteId)}`
  );
  return res.data.data;
}

// ============================================================================
// Series query + catalog (djin-cloud#806 — the Historian rebuild's data layer)
//
// ⚠️ The types below are HAND-COPIED from the backend services, not imported:
// `backend/src/services/historian-catalog.service.ts` and
// `backend/src/services/historian-series.service.ts`. web/ and backend/ are
// separate TS projects with no shared package, so this is a mirrored contract.
// When either service's exported shape changes, change this block in the same
// PR — nothing checks it mechanically.
// ============================================================================

/** One metric seen on a device in the catalog lookback window. */
export interface CatalogMetric {
  name: string;
  /**
   * From the telemetry row's `tags->>'unit'`, so it is null whenever the
   * writer did not materialize one. Callers fall back to the static
   * `METRIC_UNITS` catalog in `web/src/utils/historianMetrics.ts`.
   */
  unit: string | null;
  lastSeen: string;
}

/**
 * One browsable node: roster devices (`config.site_devices`, `removed_at IS
 * NULL`) grouped by the id their telemetry is stored under, plus telemetry no
 * roster row claims (#828).
 *
 * ⚠️ `deviceId` is the TELEMETRY id — what `/series-query` filters on — and is
 * not always a roster id: a BMS and PCS sharing block `ES-001` are ONE node
 * `ES-001` with `rosterDeviceIds: ['BMS-001', 'PCS-001']`, and Turner's
 * recloser is node `RECLOSER-001` for roster row `SEL-651R-1`. The server
 * keys roster rows on `block_id ?? device_id`, the SLD generator's rule.
 *
 * ⚠️ `metrics` is ALWAYS an array and never omitted — a device with no
 * telemetry in the lookback window comes back with `metrics: []` so the tree
 * can show it dimmed rather than hide it. An empty array is "present but
 * quiet", not "absent"; do not treat the two as the same.
 */
export interface CatalogDevice {
  deviceId: string;
  displayName: string | null;
  deviceType: string;
  blockId: string | null;
  /**
   * Roster rows folded into this node; `[]` for telemetry no roster row
   * claims (`site-summary`, or a device whose roster row names a different
   * id). Optional because `cloud-ui` and `cloud-api` deploy as separate apps:
   * during a rollout the page can briefly talk to an API from before #828.
   */
  rosterDeviceIds?: string[];
  metrics: CatalogMetric[];
}

export interface HistorianCatalog {
  siteId: string;
  generatedAt: string;
  lookbackDays: number;
  devices: CatalogDevice[];
}

/** One requested series. `alias` is the caller's opaque label — the server never parses it. */
export interface SeriesSpecItem {
  siteId: string;
  deviceId: string;
  metric: string;
  alias: string;
}

export interface SeriesQuerySpec {
  series: SeriesSpecItem[];
  /** ISO 8601. */
  startTime: string;
  /** ISO 8601. */
  endTime: string;
  /**
   * `'auto'` or one of the server's ten shorthands
   * (`1m 5m 15m 30m 1h 6h 12h 1d 7d 30d`). Typed as `string` rather than
   * mirroring the server union because the picker offers a deliberate subset
   * (`EXPLICIT_INTERVALS`); the server validates the value either way.
   */
  interval: string;
  aggregation?: 'avg' | 'min' | 'max';
  limit?: number;
}

export interface SeriesColumnMeta {
  siteId: string;
  deviceId: string;
  metric: string;
  /**
   * ⚠️ Nullable BY DESIGN, and which way it lands moved twice in djin-cloud#832.
   * The rollups do not materialize `tags`, so the rollup half of a query returns
   * null — but #845's raw TAIL FILL selects `MAX(tags->>'unit')`, so a routed
   * query resolves units whenever its window reaches past the rollup checkpoint,
   * which for a live view is always. And routing is on by default now, so the old
   * "local runs the rollup path, Azure runs raw" split is gone: a tenant whose
   * Terraform is applied runs the same path local dev does.
   * Treat the unit fallback chain as ordinary on every environment rather than
   * inferring it from whichever path one box happens to take.
   */
  unit: string | null;
}

export interface SeriesQueryResult {
  queryId: string;
  columns: string[];
  rows: Array<Record<string, number | null>>;
  columnMeta: Record<string, SeriesColumnMeta>;
  metadata: {
    /** WIDE rows — one per distinct timestamp across every site, i.e. `rows.length`. */
    rowCount: number;
    /** Wall time of the SLOWEST site, not the sum — the per-site fan-out is concurrent. */
    queryTimeMs: number;
    /** The RESOLVED shorthand (e.g. `'15m'`), never `'auto'`. */
    interval: string;
    start: string;
    end: string;
    truncated: boolean;
    /**
     * Which end the row budget dropped, when `truncated`. Always `'oldest'` — the server
     * orders newest-first under its LIMIT (djin-cloud#812), so live data survives and
     * history is what gets cut. Null when nothing was cut.
     */
    truncatedSide: 'oldest' | null;
    /**
     * ISO instant from which EVERY requested series is present. Before it, at least one
     * column is missing samples the query asked for. Null when nothing was cut.
     */
    completeFrom: string | null;
    /** Which table(s) answered; 'mixed' when sites in the fan-out disagree (djin-cloud#832). */
    source: 'raw' | 'rollup' | 'rollup+raw' | 'mixed';
    /**
     * ISO instant the result stops being complete, when a rollup was too far behind for the
     * server to fill the gap from raw. Null — the normal case — means every series reaches
     * the requested end. Opposite end from `completeFrom`; both can be set at once.
     */
    dataThrough: string | null;
    /** Worst rollup lag across the fan-out, in seconds. Null when no site read a rollup. */
    rollupLagSeconds: number | null;
    /**
     * ⚠️ `perSite[].rowCount` counts LONG-format samples kept from that site
     * and is NOT the same quantity as `metadata.rowCount` above, despite the
     * shared name. It sums to more than `rowCount` whenever several series
     * share a timestamp, which is the normal case.
     */
    perSite: Array<{ siteId: string; rowCount: number; queryTimeMs: number; truncated: boolean; oldestReturned: string | null }>;
  };
}

/** Site → device → metric catalog for the variable browser. */
export async function fetchCatalog(siteId: string): Promise<HistorianCatalog> {
  // The historian timeout, like every other historian read here. Without it this call
  // inherited the shared client's 10 s default, so a catalog that is slow but succeeds
  // on a large site would still be aborted client-side (#826).
  const res = await api.get<{ success: boolean; data: HistorianCatalog }>(
    `/v1/historian/catalog/${encodeURIComponent(siteId)}`,
    { timeout: HISTORIAN_QUERY_TIMEOUT_MS }
  );
  return res.data.data;
}

/**
 * Run a multi-site series query. The server resolves `auto`, fans out per
 * site, and persists the spec so the result is replayable by `queryId`.
 */
export async function runSeriesQuery(spec: SeriesQuerySpec): Promise<SeriesQueryResult> {
  const res = await api.post<{ success: boolean; data: SeriesQueryResult }>(
    '/v1/historian/series-query',
    spec,
    { timeout: HISTORIAN_QUERY_TIMEOUT_MS }
  );
  return res.data.data;
}

/**
 * Replay a persisted query by id.
 *
 * ⚠️ The stored spec is RE-RUN against current telemetry, not read from a
 * cache — site access is re-checked at read time, and a preset-derived window
 * is whatever the stored absolute timestamps say. An unknown id and another
 * tenant's id both 404 identically.
 */
export async function fetchPersistedQuery(id: string): Promise<SeriesQueryResult> {
  const res = await api.get<{ success: boolean; data: SeriesQueryResult }>(
    `/v1/historian/queries/${encodeURIComponent(id)}`,
    { timeout: HISTORIAN_QUERY_TIMEOUT_MS }
  );
  return res.data.data;
}
